import type { ReactiveFormSupportDataSource } from '../../interfaces/form.interface';
import type {
    ReactiveFormSupportDataReadinessItem,
    ReactiveFormSupportDataReadinessPhase,
    ReactiveFormSupportDataReadinessSeverity,
    ReactiveFormSupportDataReadinessStatus,
} from '../../components/support-data-readiness/support-data-readiness.component';
import type {
    ReactiveFormLoadSessionPhase,
    ReactiveFormLoadStateSnapshot,
} from './reactive-form-load-session';

export interface ReactiveFormSupportDataReadinessPresenterOptions<TModel = unknown> {
    readonly sources: readonly ReactiveFormSupportDataSource<TModel>[];
    readonly labels?: Readonly<Partial<Record<string, string>>> | undefined;
    readonly loadState?: ReactiveFormLoadStateSnapshot | null | undefined;
    readonly visibleStatuses?: readonly ReactiveFormSupportDataReadinessStatus[] | undefined;
    readonly includeUnknownSources?: boolean | undefined;
}

export interface ReactiveFormSupportDataReadinessViewModel {
    readonly items: readonly ReactiveFormSupportDataReadinessItem[];
    readonly severity: ReactiveFormSupportDataReadinessSeverity;
    readonly summary: string;
}

const DEFAULT_VISIBLE_STATUSES: readonly ReactiveFormSupportDataReadinessStatus[] = ['pending', 'loading', 'lazy', 'error'];

const SUPPORT_DATA_STATUS_DETAILS: Record<ReactiveFormSupportDataReadinessStatus, string> = {
    pending: 'Pendiente de cargar sin bloquear la edición.',
    loading: 'Cargando en segundo plano.',
    ready: 'Disponible.',
    lazy: 'Se cargará cuando el campo lo necesite.',
    error: 'No se ha podido cargar. Revisa la conexión o recarga el formulario.',
};

/** Builds a reusable visual readiness model from a load-session snapshot and support-data declarations. */
export class ReactiveFormSupportDataReadinessPresenter {
    public static createViewModel<TModel = unknown>(
        options: ReactiveFormSupportDataReadinessPresenterOptions<TModel>,
    ): ReactiveFormSupportDataReadinessViewModel {
        const visibleStatuses = options.visibleStatuses ?? DEFAULT_VISIBLE_STATUSES;
        const items = options.sources
            .filter((source) => options.includeUnknownSources === true || options.labels?.[source.key] !== undefined)
            .map((source): ReactiveFormSupportDataReadinessItem => {
                const phase = ReactiveFormSupportDataReadinessPresenter.normalizePhase(source.phase);
                const status = ReactiveFormSupportDataReadinessPresenter.resolveStatus(phase, options.loadState);

                return {
                    id: source.key,
                    label: options.labels?.[source.key] ?? source.key,
                    phase,
                    status,
                    detail: SUPPORT_DATA_STATUS_DETAILS[status],
                };
            })
            .filter((item) => visibleStatuses.includes(item.status));

        return {
            items,
            severity: ReactiveFormSupportDataReadinessPresenter.resolveSeverity(items),
            summary: ReactiveFormSupportDataReadinessPresenter.resolveSummary(items),
        };
    }

    private static normalizePhase(phase: string | undefined): ReactiveFormSupportDataReadinessPhase {
        if (phase === 'critical' || phase === 'lazy') {
            return phase;
        }

        return 'deferred';
    }

    private static resolveStatus(
        phase: ReactiveFormSupportDataReadinessPhase,
        loadState: ReactiveFormLoadStateSnapshot | null | undefined,
    ): ReactiveFormSupportDataReadinessStatus {
        const phaseState = loadState?.phases[phase as ReactiveFormLoadSessionPhase];

        if (phaseState?.failed === true) {
            return 'error';
        }

        if (phaseState?.loading === true) {
            return 'loading';
        }

        if (phaseState?.ready === true) {
            return 'ready';
        }

        if (phase === 'lazy') {
            return 'lazy';
        }

        return 'pending';
    }

    private static resolveSeverity(
        items: readonly ReactiveFormSupportDataReadinessItem[],
    ): ReactiveFormSupportDataReadinessSeverity {
        if (items.some((item) => item.status === 'error')) {
            return 'danger';
        }

        if (items.some((item) => item.status === 'loading')) {
            return 'info';
        }

        if (items.some((item) => item.status === 'pending')) {
            return 'warning';
        }

        return 'success';
    }

    private static resolveSummary(items: readonly ReactiveFormSupportDataReadinessItem[]): string {
        if (items.some((item) => item.status === 'error')) {
            return 'Algunos catálogos no se han cargado. Puedes continuar y revisar los pendientes.';
        }

        if (items.some((item) => item.status === 'loading')) {
            return 'El formulario ya está abierto mientras se completan catálogos secundarios.';
        }

        if (items.some((item) => item.status === 'pending')) {
            return 'Quedan catálogos secundarios pendientes de cargar.';
        }

        if (items.some((item) => item.status === 'lazy')) {
            return 'Los catálogos bajo demanda se cargarán solo cuando sean necesarios.';
        }

        return '';
    }
}
